import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api, TestRunWithProgress } from '../services/api';
import { TestExecutionWithDetails } from '../types';
import TestExecutionModal from '../components/TestExecutionModal';
import { emailToUsername, parseJiraTickets } from '../utils/formatters';
import { useConfirmDialog } from '../components/ConfirmDialog';
import { useAuth } from '../contexts/AuthContext';
import './TestRunDetailPage.css';

const PAGE_SIZE = 10;

const TestRunDetailPage: React.FC = () => {
  const { testRunId } = useParams<{ testRunId: string }>();
  const navigate = useNavigate();
  const { confirm, dialog } = useConfirmDialog();
  const { user: currentUser } = useAuth();
  const isAdmin = currentUser?.role === 'Admin';
  const [testRun, setTestRun] = useState<TestRunWithProgress | null>(null);
  const [executions, setExecutions] = useState<TestExecutionWithDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [selectedExecution, setSelectedExecution] = useState<TestExecutionWithDetails | null>(null);
  const [showExecutionModal, setShowExecutionModal] = useState(false);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [reopening, setReopening] = useState(false);

  // Evidence download (for immutable runs)
  const [evidenceDropdown, setEvidenceDropdown] = useState<string | null>(null); // executionId with open dropdown
  const [evidenceMap, setEvidenceMap] = useState<Record<string, any[]>>({}); // executionId → evidence list
  const [evidenceLoading, setEvidenceLoading] = useState<Record<string, boolean>>({}); // executionId → loading
  const [showEvidenceModal, setShowEvidenceModal] = useState(false);
  const [selectedEvidenceExecution, setSelectedEvidenceExecution] = useState<string | null>(null);

  // Bulk evidence upload
  const [selectedExecutionIds, setSelectedExecutionIds] = useState<Set<string>>(new Set());
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [bulkFile, setBulkFile] = useState<File | null>(null);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkProgress, setBulkProgress] = useState<{ done: number; total: number } | null>(null);
  const [bulkError, setBulkError] = useState<string | null>(null);
  const bulkFileInputRef = useRef<HTMLInputElement>(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (testRunId) {
      loadTestRun();
      loadExecutions();
    }
  }, [testRunId, statusFilter]);

  const loadTestRun = async () => {
    try {
      const run = await api.getTestRun(testRunId!);
      setTestRun(run);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to load test run');
    }
  };

  const loadExecutions = async () => {
    try {
      setLoading(true);
      const execs = await api.getTestRunExecutions(testRunId!, statusFilter || undefined);
      setExecutions(execs);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to load executions');
    } finally {
      setLoading(false);
    }
  };

  const handleStartRun = async () => {
    try {
      await api.startTestRun(testRunId!);
      await loadTestRun();
    } catch (err: any) {
      setError(err.message || 'Failed to start test run');
    }
  };

  const handleCloseRun = async () => {
    const confirmed = await confirm(
      'Close Test Run',
      'Are you sure you want to close this test run? It will become immutable and no further changes can be made.',
      'Close Test Run',
      'lock',
      false   // not danger — this is a finalize, not a delete
    );
    if (!confirmed) return;
    try {
      await api.closeTestRun(testRunId!);
      await loadTestRun();
    } catch (err: any) {
      setError(err.message || 'Failed to close test run');
    }
  };

  const handleReopenRun = async () => {
    const confirmed = await confirm(
      'Reopen Test Run',
      'This will reopen the test run and set it back to IN PROGRESS. You can then upload evidence and re-execute test cases. Only do this if the run was closed by mistake.',
      'Reopen Run',
      'warning',
      false
    );
    if (!confirmed) return;
    try {
      setReopening(true);
      await api.reopenTestRun(testRunId!);
      setSuccessMessage('Test run has been reopened and is now IN PROGRESS.');
      await loadTestRun();
    } catch (err: any) {
      setError(err?.response?.data?.detail || err.message || 'Failed to reopen test run');
    } finally {
      setReopening(false);
    }
  };

  const handleSyncNew = async () => {    try {
      setSyncing(true);
      setSuccessMessage(null);
      const result = await api.syncNewToTestRun(testRunId!);
      if (result.added === 0) {
        setSuccessMessage('Already up to date — no new scenarios to sync.');
      } else {
        setSuccessMessage(`${result.added} new scenario${result.added !== 1 ? 's' : ''} added to the test run.`);
        await loadTestRun();
        await loadExecutions();
      }
    } catch (err: any) {
      setError(err.message || 'Failed to sync new scenarios');
    } finally {
      setSyncing(false);
    }
  };

  // Selection helpers
  const toggleSelectExecution = (id: string) => {
    setSelectedExecutionIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedExecutionIds.size === paginated.length) {
      setSelectedExecutionIds(new Set());
    } else {
      setSelectedExecutionIds(new Set(paginated.map(e => e.id)));
    }
  };

  const handleBulkUpload = async () => {
    if (!bulkFile || selectedExecutionIds.size === 0) return;
    setBulkUploading(true);
    setBulkError(null);
    const ids = Array.from(selectedExecutionIds);
    setBulkProgress({ done: 0, total: ids.length });
    let failed = 0;

    // Resolve the display ID of the first execution (for the reference note)
    const firstExec = executions.find(e => e.id === ids[0]);
    const firstDisplayId = firstExec?.test_case_display_id || ids[0].slice(0, 8);

    for (let i = 0; i < ids.length; i++) {
      try {
        if (i === 0) {
          // First execution — mark PASS + upload the file
          await api.updateExecution(ids[i], { execution_status: 'PASS' });
          await api.uploadExecutionEvidence(ids[i], bulkFile, 0);
        } else {
          // Remaining executions — mark PASS + set a reference note, NO file upload
          await api.updateExecution(ids[i], {
            execution_status: 'PASS',
            defect_reference: `Evidence same as ${firstDisplayId}`,
          });
        }
      } catch {
        failed++;
      }
      setBulkProgress({ done: i + 1, total: ids.length });
    }
    setBulkUploading(false);
    if (failed > 0) {
      setBulkError(`${failed} upload(s) failed. ${ids.length - failed} succeeded.`);
    } else {
      setShowBulkUpload(false);
      setBulkFile(null);
      setBulkProgress(null);
      setSelectedExecutionIds(new Set());
      await loadTestRun();
      await loadExecutions();
    }
  };

  const handleExecuteTestCase = (execution: TestExecutionWithDetails) => {
    setSelectedExecution(execution);
    setShowExecutionModal(true);
  };

  const handleExecutionComplete = () => {
    setShowExecutionModal(false);
    setSelectedExecution(null);
    loadTestRun();
    loadExecutions();
  };

  // Close evidence dropdown when clicking outside (no longer needed with modal)
  // Keeping for reference but not used anymore

  const handleEvidenceDownloadClick = async (executionId: string) => {
    // If already loaded, show modal
    if (evidenceMap[executionId] !== undefined) {
      const files = evidenceMap[executionId];
      if (files.length === 0) return; // nothing to do
      if (files.length === 1) {
        triggerEvidenceDownload(files[0]);
        return;
      }
      setSelectedEvidenceExecution(executionId);
      setShowEvidenceModal(true);
      return;
    }

    // Fetch evidence list
    setEvidenceLoading(prev => ({ ...prev, [executionId]: true }));
    try {
      const files = await api.getExecutionEvidence(executionId);
      setEvidenceMap(prev => ({ ...prev, [executionId]: files }));
      if (files.length === 0) {
        // nothing — button will become disabled on next render
      } else if (files.length === 1) {
        triggerEvidenceDownload(files[0]);
      } else {
        setSelectedEvidenceExecution(executionId);
        setShowEvidenceModal(true);
      }
    } catch {
      // silently ignore — button stays enabled for retry
    } finally {
      setEvidenceLoading(prev => ({ ...prev, [executionId]: false }));
    }
  };

  const triggerEvidenceDownload = async (evidence: any) => {
    try {
      // Step 1: get the URL info via the authenticated API
      const urlInfo = await api.getEvidenceUrl(evidence.id);

      if (urlInfo.storage === 's3' && urlInfo.url) {
        // S3: presigned URL — open directly, no auth header needed
        const a = document.createElement('a');
        a.href = urlInfo.url;
        a.download = urlInfo.file_name || evidence.file_name || evidence.id;
        a.target = '_blank';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      } else {
        // Local: fetch as blob through the authenticated axios client
        const axiosInstance = (api as any).client;
        const response = await axiosInstance.get(
          `/api/v1/test-runs/evidence/${evidence.id}/download`,
          { responseType: 'blob' }
        );
        const blob: Blob = response.data;
        const objectUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = objectUrl;
        a.download = urlInfo.file_name || evidence.file_name || evidence.id;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(objectUrl), 10_000);
      }
    } catch (err: any) {
      setError(`Failed to download evidence: ${err?.message || 'Unknown error'}`);
    }
  };

  const getRunStatusClass = (status: string) => {
    switch (status) {
      case 'IN_PROGRESS': return 'run-status-inprogress';
      case 'COMPLETED':   return 'run-status-completed';
      case 'ABORTED':     return 'run-status-aborted';
      default:            return 'run-status-notstarted';
    }
  };

  const getRunStatusLabel = (status: string) => {
    switch (status) {
      case 'IN_PROGRESS': return 'IN PROGRESS';
      case 'NOT_STARTED': return 'NOT STARTED';
      default: return status;
    }
  };

  const getExecStatusClass = (status: string) => {
    switch (status) {
      case 'PASS':    return 'exec-pass';
      case 'FAIL':    return 'exec-fail';
      case 'BLOCKED': return 'exec-blocked';
      default:        return 'exec-notrun';
    }
  };

  const formatTime = (dateStr?: string) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDateLine = (dateStr?: string) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' })
      + ' · ' + new Date(dateStr).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  if (loading && !testRun) {
    return <div className="trd-page"><div className="trd-loading">Loading...</div></div>;
  }
  if (!testRun) {
    return <div className="trd-page"><div className="trd-error">Test run not found</div></div>;
  }

  const isImmutable = testRun.status === 'COMPLETED' || testRun.status === 'ABORTED';

  // Progress bar segments
  const total = testRun.total_cases || 1;
  const passW    = Math.round((testRun.pass_count / total) * 100);
  const failW    = Math.round((testRun.fail_count / total) * 100);
  const blockedW = Math.round((testRun.blocked_count / total) * 100);
  const notRunW  = Math.max(0, 100 - passW - failW - blockedW);

  // Strip HTML tags for plain-text search/display
  const stripHtml = (html: string) => html.replace(/<[^>]*>/g, '');

  // Filter + search + paginate
  const filtered = executions
    .filter(e => {
      const matchSearch = !searchTerm ||
        stripHtml(e.test_case_description || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        e.test_case_display_id?.toLowerCase().includes(searchTerm.toLowerCase());
      return matchSearch;
    })
    .sort((a, b) => {
      // Sort by numeric part of ID (TS-00153 → 153, TC-00153 → 153)
      const na = parseInt((a.test_case_display_id || '').replace(/\D/g, '')) || 0;
      const nb = parseInt((b.test_case_display_id || '').replace(/\D/g, '')) || 0;
      return na - nb;
    });

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const STATUS_OPTIONS = [
    { value: '', label: 'All Statuses' },
    { value: 'PASS', label: 'Pass' },
    { value: 'FAIL', label: 'Fail' },
    { value: 'BLOCKED', label: 'Blocked' },
    { value: 'NOT_RUN', label: 'Not Run' },
  ];

  const currentStatusLabel = STATUS_OPTIONS.find(o => o.value === statusFilter)?.label || 'All Statuses';

  return (
    <div className="trd-page">
      {dialog}
      {/* Back link */}
      <button className="trd-back" onClick={() => navigate('/test-runs')}>
        ← Back to Test Runs
      </button>

      {error && <div className="trd-error-banner">{error}<button onClick={() => setError(null)}>×</button></div>}
      {successMessage && <div className="trd-success-banner">{successMessage}<button onClick={() => setSuccessMessage(null)}>×</button></div>}

      {/* Title row */}
      <div className="trd-title-row">
        <div className="trd-title-left">
          <h1 className="trd-title">{testRun.name}</h1>
          <span className={`trd-run-status ${getRunStatusClass(testRun.status)}`}>
            {getRunStatusLabel(testRun.status)}
          </span>
        </div>
        <div className="trd-title-actions">
          {testRun.status === 'NOT_STARTED' && (
            <button className="trd-btn-start" onClick={handleStartRun}>▶ Start Execution</button>
          )}
          {(testRun.status === 'NOT_STARTED' || testRun.status === 'IN_PROGRESS') && (
            <button
              className="trd-btn-sync"
              onClick={handleSyncNew}
              disabled={syncing}
              title="Add scenarios/test cases created after this run was created"
            >
              {syncing ? '⟳ Syncing...' : '⟳ Sync New'}
            </button>
          )}
          {testRun.status === 'IN_PROGRESS' && (
            <button className="trd-btn-close" onClick={handleCloseRun}>Close Test Run</button>
          )}
          {(testRun.status === 'COMPLETED' || testRun.status === 'ABORTED') && isAdmin && (
            <button
              className="trd-btn-reopen"
              onClick={handleReopenRun}
              disabled={reopening}
              title="Admin only: reopen this run to allow further execution and evidence upload"
            >
              {reopening ? '↩ Reopening...' : '↩ Reopen Run'}
            </button>
          )}
        </div>
      </div>

      {/* Info + Progress row */}
      <div className="trd-top-row">
        {/* Execution context */}
        <div className="trd-context-card">
          <div className="trd-context-label">EXECUTION CONTEXT</div>
          <div className="trd-context-row">
            <span className="trd-ctx-key">Build Version</span>
            <span className="trd-ctx-val trd-ctx-mono">{testRun.build_version || '—'}</span>
          </div>
          <div className="trd-context-row">
            <span className="trd-ctx-key">Environment</span>
            <span className="trd-ctx-val trd-ctx-bold">{testRun.environment || '—'}</span>
          </div>
          <div className="trd-context-row">
            <span className="trd-ctx-key">Created By</span>
            <div className="trd-ctx-val">
              <span className="trd-ctx-bold">{emailToUsername(testRun.created_by)}</span>
              <span className="trd-ctx-sub">{formatDateLine(testRun.created_at)}</span>
            </div>
          </div>
          {testRun.started_at && (
            <div className="trd-context-row">
              <span className="trd-ctx-key">Started By</span>
              <div className="trd-ctx-val">
                <span className="trd-ctx-bold">{testRun.started_by ? emailToUsername(testRun.started_by) : 'System'}</span>
                <span className="trd-ctx-sub">{formatDateLine(testRun.started_at)}</span>
              </div>
            </div>
          )}
        </div>

        {/* Global progress */}
        <div className="trd-progress-card">
          <div className="trd-progress-top">
            <span className="trd-progress-label">GLOBAL PROGRESS</span>
            <span className="trd-progress-pct">
              <strong>{Math.round(testRun.progress_percentage)}%</strong>
              <span className="trd-progress-complete"> Complete</span>
            </span>
          </div>

          {/* Segmented progress bar */}
          <div className="trd-progress-bar">
            {passW > 0    && <div className="trd-seg trd-seg-pass"    style={{ width: `${passW}%` }} />}
            {failW > 0    && <div className="trd-seg trd-seg-fail"    style={{ width: `${failW}%` }} />}
            {blockedW > 0 && <div className="trd-seg trd-seg-blocked" style={{ width: `${blockedW}%` }} />}
            {notRunW > 0  && <div className="trd-seg trd-seg-notrun"  style={{ width: `${notRunW}%` }} />}
          </div>

          {/* Metric pills */}
          <div className="trd-metrics">
            <div className="trd-metric">
              <span className="trd-metric-label">TOTAL</span>
              <span className="trd-metric-val">{testRun.total_cases}</span>
            </div>
            <div className="trd-metric trd-metric-pass">
              <span className="trd-metric-label">PASSED</span>
              <span className="trd-metric-val">{testRun.pass_count}</span>
            </div>
            <div className="trd-metric trd-metric-fail">
              <span className="trd-metric-label">FAILED</span>
              <span className="trd-metric-val">{testRun.fail_count}</span>
            </div>
            <div className="trd-metric trd-metric-blocked">
              <span className="trd-metric-label">BLOCKED</span>
              <span className="trd-metric-val">{testRun.blocked_count}</span>
            </div>
            <div className="trd-metric trd-metric-notrun">
              <span className="trd-metric-label">NOT RUN</span>
              <span className="trd-metric-val">{testRun.not_run_count}</span>
            </div>
            <div className="trd-metric trd-metric-rate">
              <span className="trd-metric-label">PASS RATE</span>
              <span className="trd-metric-val">{testRun.pass_percentage.toFixed(1)}%</span>
            </div>
          </div>
        </div>
      </div>

      {/* Executions table */}
      <div className="trd-table-card">
        <div className="trd-table-header">
          <h2 className="trd-table-title">Test Execution Details</h2>
          <div className="trd-table-controls">
            {/* Status filter dropdown */}
            <div
              className="trd-status-filter"
              onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setShowStatusDropdown(false); }}
            >
              <button
                className="trd-filter-btn"
                onClick={() => setShowStatusDropdown(v => !v)}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
                </svg>
                {currentStatusLabel}
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M6 9l6 6 6-6"/>
                </svg>
              </button>
              {showStatusDropdown && (
                <div className="trd-filter-dropdown">
                  {STATUS_OPTIONS.map(opt => (
                    <button
                      key={opt.value}
                      className={`trd-filter-option${statusFilter === opt.value ? ' selected' : ''}`}
                      onClick={() => { setStatusFilter(opt.value); setShowStatusDropdown(false); setPage(1); }}
                    >
                      {statusFilter === opt.value && (
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                          <polyline points="20 6 9 17 4 12"/>
                        </svg>
                      )}
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Search */}
            <div className="trd-search-wrap">
              <svg className="trd-search-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
              <input
                className="trd-search"
                type="text"
                placeholder="Search test cases..."
                value={searchTerm}
                onChange={e => { setSearchTerm(e.target.value); setPage(1); }}
              />
            </div>
          </div>
        </div>

        {loading ? (
          <div className="trd-loading">Loading test cases...</div>
        ) : (
          <>
            {/* Bulk action bar — shown when rows are selected */}
            {!isImmutable && selectedExecutionIds.size > 0 && (
              <div className="trd-bulk-bar">
                <span className="trd-bulk-count">{selectedExecutionIds.size} selected</span>
                <button
                  className="trd-bulk-btn"
                  onClick={() => { setBulkFile(null); setBulkError(null); setBulkProgress(null); setShowBulkUpload(true); }}
                >
                  📎 Upload Evidence to Selected ({selectedExecutionIds.size})
                </button>
                <button className="trd-bulk-clear" onClick={() => setSelectedExecutionIds(new Set())}>
                  Clear selection
                </button>
              </div>
            )}
            <table className="trd-table">
              <thead>
                <tr>
                  {!isImmutable && (
                    <th className="trd-th-check">
                      <input
                        type="checkbox"
                        className="trd-checkbox"
                        checked={paginated.length > 0 && selectedExecutionIds.size === paginated.length}
                        onChange={toggleSelectAll}
                        title="Select all on this page"
                      />
                    </th>
                  )}
                  <th>TEST CASE &amp; ID</th>
                  <th>STATUS</th>
                  <th>EXECUTED BY</th>
                  <th>EXECUTED AT</th>
                  <th>DEFECT / NOTE</th>
                  <th>ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {paginated.length === 0 ? (
                  <tr>
                    <td colSpan={isImmutable ? 6 : 7} className="trd-empty">No test cases found</td>
                  </tr>
                ) : paginated.map(execution => (
                  <tr key={execution.id} className={selectedExecutionIds.has(execution.id) ? 'trd-row-selected' : ''}>
                    {!isImmutable && (
                      <td className="trd-td-check">
                        <input
                          type="checkbox"
                          className="trd-checkbox"
                          checked={selectedExecutionIds.has(execution.id)}
                          onChange={() => toggleSelectExecution(execution.id)}
                        />
                      </td>
                    )}
                    <td>
                      <div
                        className="trd-tc-name"
                        dangerouslySetInnerHTML={{ __html: execution.test_case_description || '' }}
                      />
                      {execution.test_case_display_id && (
                        <div className="trd-tc-id">{execution.test_case_display_id}</div>
                      )}
                    </td>
                    <td>
                      <span className={`trd-exec-badge ${getExecStatusClass(execution.execution_status)}`}>
                        {execution.execution_status.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="trd-td-muted">{execution.executed_by ? emailToUsername(execution.executed_by) : '—'}</td>
                    <td className="trd-td-muted">{formatTime(execution.executed_at)}</td>
                    <td>
                      {execution.defect_reference
                        ? <span className="trd-defect-ref">
                            {parseJiraTickets(execution.defect_reference).map((seg, i) =>
                              typeof seg === 'string'
                                ? <React.Fragment key={i}>{seg}</React.Fragment>
                                : <a
                                    key={i}
                                    href={seg.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="trd-defect-jira-link"
                                    title={`Open ${seg.ticket} in Jira`}
                                    onClick={e => e.stopPropagation()}
                                  >{seg.ticket}</a>
                            )}
                          </span>
                        : <span className="trd-td-muted">—</span>
                      }
                    </td>
                    <td>
                      {!isImmutable ? (
                        <button
                          className="trd-action-btn"
                          onClick={() => handleExecuteTestCase(execution)}
                          title={execution.execution_status === 'NOT_RUN' ? 'Execute' : 'Re-execute'}
                        >
                          {execution.execution_status === 'NOT_RUN' ? (
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <polygon points="5 3 19 12 5 21 5 3"/>
                            </svg>
                          ) : (
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 102.13-9.36L1 10"/>
                            </svg>
                          )}
                        </button>
                      ) : (
                        /* Immutable run — show evidence download button or reference note */
                        (() => {
                          // If this execution is a bulk-upload reference (no actual file),
                          // hide the download button — the DEFECT/NOTE column already shows the reference
                          const isEvidenceRef = execution.defect_reference?.startsWith('Evidence same as');
                          if (isEvidenceRef) {
                            return <span className="trd-td-muted">—</span>;
                          }

                          const isLoadingEvidence = evidenceLoading[execution.id];
                          const files = evidenceMap[execution.id]; // undefined = not yet fetched
                          const hasNoEvidence = files !== undefined && files.length === 0;

                          return (
                            <button
                              className={`trd-action-btn trd-download-btn${hasNoEvidence ? ' trd-download-btn--disabled' : ''}`}
                              onClick={() => !hasNoEvidence && handleEvidenceDownloadClick(execution.id)}
                              disabled={isLoadingEvidence || hasNoEvidence}
                              title={
                                isLoadingEvidence ? 'Loading evidence...'
                                : hasNoEvidence ? 'No evidence uploaded'
                                : 'Download evidence'
                              }
                            >
                              {isLoadingEvidence ? (
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="trd-spin">
                                  <path d="M21 12a9 9 0 11-6.219-8.56"/>
                                </svg>
                              ) : (
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
                                  <polyline points="7 10 12 15 17 10"/>
                                  <line x1="12" y1="15" x2="12" y2="3"/>
                                </svg>
                              )}
                            </button>
                          );
                        })()
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination */}
            <div className="trd-pagination">
              <span className="trd-pagination-info">
                Showing {Math.min((page - 1) * PAGE_SIZE + 1, filtered.length)}–{Math.min(page * PAGE_SIZE, filtered.length)} of {filtered.length} test cases
              </span>
              <div className="trd-pagination-btns">
                <button
                  className="trd-page-btn"
                  disabled={page === 1}
                  onClick={() => setPage(p => p - 1)}
                >
                  Previous
                </button>
                <button
                  className="trd-page-btn"
                  disabled={page >= totalPages}
                  onClick={() => setPage(p => p + 1)}
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {showExecutionModal && selectedExecution && (
        <TestExecutionModal
          execution={selectedExecution}
          onClose={() => setShowExecutionModal(false)}
          onComplete={handleExecutionComplete}
        />
      )}

      {/* Evidence Files Modal */}
      {showEvidenceModal && selectedEvidenceExecution && evidenceMap[selectedEvidenceExecution] && (
        <div className="trd-evidence-modal-overlay" onClick={() => setShowEvidenceModal(false)}>
          <div className="trd-evidence-modal" onClick={(e) => e.stopPropagation()}>
            <div className="trd-evidence-modal-header">
              <h3>Evidence Files</h3>
              <button className="trd-evidence-modal-close" onClick={() => setShowEvidenceModal(false)}>×</button>
            </div>
            <div className="trd-evidence-modal-body">
              {evidenceMap[selectedEvidenceExecution].length === 0 ? (
                <p className="trd-evidence-empty">No evidence files uploaded</p>
              ) : (
                <div className="trd-evidence-list">
                  {evidenceMap[selectedEvidenceExecution].map((file: any) => (
                    <button
                      key={file.id}
                      className="trd-evidence-file-item"
                      onClick={() => {
                        triggerEvidenceDownload(file);
                        setShowEvidenceModal(false);
                      }}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
                        <polyline points="7 10 12 15 17 10"/>
                        <line x1="12" y1="15" x2="12" y2="3"/>
                      </svg>
                      <div className="trd-evidence-file-info">
                        <div className="trd-evidence-file-name">{file.file_name || file.id}</div>
                        <div className="trd-evidence-file-date">{new Date(file.uploaded_at).toLocaleDateString()}</div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Bulk Evidence Upload Modal */}
      {showBulkUpload && (
        <div className="trd-bulk-overlay" onClick={() => { if (!bulkUploading) setShowBulkUpload(false); }}>
          <div className="trd-bulk-modal" onClick={e => e.stopPropagation()}>
            <div className="trd-bulk-modal-header">
              <h3>Upload Evidence to {selectedExecutionIds.size} Execution{selectedExecutionIds.size !== 1 ? 's' : ''}</h3>
              <button className="trd-bulk-modal-close" onClick={() => setShowBulkUpload(false)} disabled={bulkUploading}>×</button>
            </div>
            <div className="trd-bulk-modal-body">
              <p className="trd-bulk-desc">
                The file will be uploaded once (to the first selected execution).
                All {selectedExecutionIds.size} execution{selectedExecutionIds.size !== 1 ? 's' : ''} will be marked as <strong style={{ color: '#16a34a' }}>PASS</strong>.
                The remaining {selectedExecutionIds.size - 1} will have a note added in the <strong>Defect / Note</strong> column referencing the first execution's evidence.
                {testRun?.environment && <span className="trd-bulk-env"> Environment: <strong>{testRun.environment}</strong></span>}
              </p>

              <div className="trd-bulk-file-area" onClick={() => bulkFileInputRef.current?.click()}>
                <input
                  ref={bulkFileInputRef}
                  type="file"
                  accept="image/*,video/*"
                  style={{ display: 'none' }}
                  onChange={e => {
                    const f = e.target.files?.[0];
                    if (f) { setBulkFile(f); setBulkError(null); }
                  }}
                />
                {bulkFile ? (
                  <div className="trd-bulk-file-selected">
                    <span className="trd-bulk-file-icon">{bulkFile.type.startsWith('video/') ? '🎥' : '🖼️'}</span>
                    <div>
                      <div className="trd-bulk-file-name">{bulkFile.name}</div>
                      <div className="trd-bulk-file-size">{(bulkFile.size / 1024 / 1024).toFixed(2)} MB</div>
                    </div>
                    <button className="trd-bulk-file-change" onClick={e => { e.stopPropagation(); bulkFileInputRef.current?.click(); }}>Change</button>
                  </div>
                ) : (
                  <div className="trd-bulk-file-placeholder">
                    <span style={{ fontSize: '2rem' }}>📎</span>
                    <span>Click to select an image or video (max 200MB)</span>
                  </div>
                )}
              </div>

              {bulkProgress && (
                <div className="trd-bulk-progress">
                  <div className="trd-bulk-progress-bar">
                    <div
                      className="trd-bulk-progress-fill"
                      style={{ width: `${Math.round((bulkProgress.done / bulkProgress.total) * 100)}%` }}
                    />
                  </div>
                  <span className="trd-bulk-progress-text">
                    {bulkUploading
                      ? `Uploading ${bulkProgress.done} / ${bulkProgress.total}...`
                      : `Done — ${bulkProgress.done} / ${bulkProgress.total} uploaded`}
                  </span>
                </div>
              )}

              {bulkError && <div className="trd-bulk-error">{bulkError}</div>}
            </div>
            <div className="trd-bulk-modal-footer">
              <button className="trd-bulk-cancel" onClick={() => setShowBulkUpload(false)} disabled={bulkUploading}>Cancel</button>
              <button
                className="trd-bulk-upload-btn"
                onClick={handleBulkUpload}
                disabled={!bulkFile || bulkUploading}
              >
                {bulkUploading ? `Uploading ${bulkProgress?.done}/${bulkProgress?.total}...` : `📤 Upload to ${selectedExecutionIds.size} Execution${selectedExecutionIds.size !== 1 ? 's' : ''}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TestRunDetailPage;
